import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, FileUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isCandidate = user && user !== false && user.role === "candidate";
  const loggedIn = user && user !== false;
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [lookupKey, setLookupKey] = useState("");
  const [uploadState, setUploadState] = useState("idle");
  const fileRef = useRef(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("error");
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.lookup_key) setLookupKey(data.lookup_key);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      } catch {}
      if (attempts.current >= 8) return setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const uploadCv = async (file) => {
    if (!file) return;
    setUploadState("uploading");
    try {
      const content_base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post("/public/cv-review-upload", { session_id: sessionId, filename: file.name, content_base64 });
      setUploadState("done");
    } catch (e) {
      toast.error(formatApiError(e));
      setUploadState("idle");
    }
  };

  const isCvReview = lookupKey === "resume_review_builder";
  const paidTitle = isCvReview ? "Payment confirmed — we're on it!" : "You're a partner!";

  return (
    <div className="min-h-screen bg-[#f5f3ee] flex items-center justify-center px-6">
      <div className="bg-white rounded-lg border border-[#e3e0d3] p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={38} className="mx-auto animate-spin text-[#0d2f4d]" />
            <h1 className="text-xl font-bold text-[#0d2f4d] mt-5">Confirming your payment...</h1>
            <p className="text-sm text-[#51637a] mt-2">Hold tight — this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={42} className="mx-auto text-[#7C8514]" />
            <h1 className="text-xl font-bold text-[#0d2f4d] mt-5" data-testid="payment-confirmed">{paidTitle}</h1>
            {isCvReview && !loggedIn ? (
              uploadState === "done" ? (
                <div className="mt-4" data-testid="guest-cv-uploaded">
                  <p className="flex items-center justify-center gap-2 text-sm font-medium text-[#0d2f4d]">
                    <Sparkles size={15} className="text-[#7C8514]" /> CV received — drafting underway.
                  </p>
                  <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
                    Our AI is writing your new Australian-market CV right now, and one of our recruiters will
                    review and sign it off before it lands in your inbox — within 48 hours.
                  </p>
                </div>
              ) : (
                <div className="mt-4">
                  <p className="text-sm text-[#51637a] leading-relaxed">
                    One last step — upload your current CV so we can get started.
                  </p>
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden"
                    data-testid="guest-cv-file-input" onChange={(e) => uploadCv(e.target.files?.[0])} />
                  <button onClick={() => fileRef.current?.click()} disabled={uploadState === "uploading"}
                    data-testid="guest-cv-upload-button"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#C1CC24] bg-[#C1CC24]/10 text-[#0d2f4d] font-medium text-sm px-6 py-6 hover:bg-[#C1CC24]/20 transition-colors disabled:opacity-60">
                    {uploadState === "uploading" ? <Loader2 size={17} className="animate-spin" /> : <FileUp size={17} />}
                    {uploadState === "uploading" ? "Uploading..." : "Upload my CV (PDF or Word)"}
                  </button>
                  <p className="text-xs text-[#51637a] mt-3">
                    Prefer email? Send it to hello@theditchcareers.com.au and we'll take it from there.
                  </p>
                </div>
              )
            ) : (
              <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
                {isCvReview
                  ? "Your Resume Review & Builder is underway. Our AI drafts your new Australian-market CV and a recruiter signs it off — it'll be with you within 48 hours."
                  : "Your subscription is active. Your recruiter will be in touch to kick off your pipeline."}
              </p>
            )}
            <Link to={isCvReview ? (isCandidate ? "/candidate" : "/") : "/employer"} data-testid="back-to-dashboard"
              className="inline-flex mt-7 rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-7 py-3 hover:bg-[#1c4d78] transition-colors">
              {isCvReview && !isCandidate ? "Back to the site" : "Back to dashboard"}
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <AlertTriangle size={38} className="mx-auto text-amber-500" />
            <h1 className="text-xl font-bold text-[#0d2f4d] mt-5">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-sm text-[#51637a] mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Check back shortly — if the issue persists, contact us."
                : "Something went wrong with your payment. You haven't been charged if the payment failed."}
            </p>
            <Link to={isCandidate ? "/candidate/onboarding?step=0" : loggedIn ? "/employer/partnership" : "/candidates"}
              className="inline-flex mt-7 rounded-full border border-[#0d2f4d] text-[#0d2f4d] text-sm font-medium px-7 py-3 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              {isCandidate ? "Back to my profile" : loggedIn ? "Back to plans" : "Back to the site"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
